import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useGetProfileSuspense, useListAccountEventsSuspense } from "@/api/bale";
import { ButtonLink } from "@/components/Button";
import { Card, CardActionsHeader, CardTextContent } from "@/components/Card";
import { Missing } from "@/components/Missing";
import { SubtleButtonLink } from "@/components/SubtleButton";
import { TextLoading } from "@/components/TextLoading";

export function HostedEventsCard({ accountId, className }: { accountId: string; className?: string }) {
  const { t } = useTranslation();

  return (
    <Card className={className}>
      <CardActionsHeader title={t("hosted-events")}>
        <ButtonLink to={`/profile/${accountId}/events`}>{t("see-all")}</ButtonLink>
      </CardActionsHeader>
      <Suspense
        fallback={
          <CardTextContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <TextLoading className="w-full" />
            </div>
            <div className="flex flex-col gap-2 border-t border-divider pt-4">
              <h3 className="font-hint">{t("previous-events")}</h3>
              <TextLoading className="w-full" />
            </div>
          </CardTextContent>
        }
      >
        <EventList accountId={accountId} />
      </Suspense>
    </Card>
  );
}

function EventList({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });
  const upcomingEvents = useListAccountEventsSuspense(
    me.data.id === accountId ? "@me" : accountId,
    { event_status: ["Upcoming"], limit: 5 },
    { query: { select: (data) => data.data } },
  );
  const pastEvents = useListAccountEventsSuspense(
    me.data.id === accountId ? "@me" : accountId,
    { event_status: ["Complete"], limit: 3 },
    { query: { select: (data) => data.data } },
  );
  return (
    <CardTextContent className="@container flex flex-col gap-4 whitespace-nowrap">
      <div className="flex flex-col gap-2">
        {upcomingEvents.data.events.slice(0, 5).map(({ event, current_attendees, pending_invites }) => (
          <Link to={`/event/${event.id}`} key={event.id}>
            <div className="flex flex-row flex-wrap items-center gap-x-4">
              <div className="mr-auto min-w-8 truncate font-interest">{event.name || t("untitled-event")}</div>
              <div className="hidden text-sm @min-[16rem]:block">
                (
                {pending_invites
                  ? t("event-attendance-with-invites", {
                      current: current_attendees,
                      max: event.max_attendees,
                      invited: pending_invites,
                    })
                  : t("event-attendance", { current: current_attendees, max: event.max_attendees })}
                )
              </div>
              <div className="hidden justify-self-end text-placeholder @min-[12rem]:block">
                {t("date-short", { date: event.start_time! })}
              </div>
            </div>
          </Link>
        ))}
        {upcomingEvents.data.events.length === 0 && <Missing>{t("not-hosting-any-events")}</Missing>}
        {upcomingEvents.data.page_info.total_count > 5 && (
          <SubtleButtonLink className="self-center" to={`/profile/${accountId}/events?tab=upcoming`}>
            {t("plus-more", { count: upcomingEvents.data.page_info.total_count - 5 })}
          </SubtleButtonLink>
        )}
      </div>
      {pastEvents.data.events.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-divider pt-4">
          <h3 className="font-hint">{t("previous-events")}</h3>
          {pastEvents.data.events.map(({ event, current_attendees }) => (
            <Link to={`/event/${event.id}`} key={event.id}>
              <div className="flex flex-row items-center gap-4">
                <div className="min-w-8 truncate font-disinterest">{event.name || t("untitled-event")}</div>
                <div className="ml-auto text-sm">
                  (
                  {t("event-past-attendance", {
                    current: current_attendees,
                    max: event.max_attendees,
                  })}
                  )
                </div>
                <div className="text-sm text-placeholder">{t("date-short", { date: event.start_time! })}</div>
              </div>
            </Link>
          ))}
          {pastEvents.data.page_info.total_count > 3 && (
            <SubtleButtonLink className="self-center" to={`/profile/${accountId}/events?tab=completed`}>
              {t("plus-more", { count: pastEvents.data.page_info.total_count - 3 })}
            </SubtleButtonLink>
          )}
        </div>
      )}
    </CardTextContent>
  );
}
